// packages/web/src/components/SubmissionStartSheet.tsx — B20a §3: "+ Submit
// for something."
//
// THE SHAPE: paste a link first — the from-link route reads it with the
// existing capture extraction and prefills the draft. Only on a genuine
// `link_unreadable` refusal does this fall to the manual fields, exactly as
// the brief states it ("On link_unreadable, show the server's message, then
// the manual fields"). A person with no link to paste can still reach the
// manual fields honestly: pasting anything that is not a real reachable
// page resolves to the same server refusal, because there is nothing else
// for it to resolve to.
//
// "Where did you find it?" rides beside the manual fields, never gates them
// — a submission is creatable with nothing (F1 §1), so provenance is
// offered, not required, at creation time. It matters later, on Free, at
// send (`259f0564`) — this sheet only records it when the artist actually
// says.
import { useState } from 'preact/hooks';
import {
  ApiError,
  createSubmission,
  createSubmissionFromLink,
  setSubmissionProvenance,
  uploadMedia,
} from '../lib/api';
import './SubmissionStartSheet.css';

export interface SubmissionStartSheetProps {
  tenantId: string;
  onClose: () => void;
  /** Fires once the draft exists — the caller opens it. */
  onCreated: (submissionId: string) => void;
}

type WhereFound = 'upload' | 'tell' | null;

export function SubmissionStartSheet({ tenantId, onClose, onCreated }: SubmissionStartSheetProps) {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [unreadable, setUnreadable] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  // ---- the manual fields, shown once the link comes back unreadable ----
  const [opportunity, setOpportunity] = useState('');
  const [project, setProject] = useState('');
  const [part, setPart] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [due, setDue] = useState('');
  const [whereFound, setWhereFound] = useState<WhereFound>(null);
  const [statement, setStatement] = useState('');
  const [file, setFile] = useState<File | null>(null);

  async function submitLink(e: Event) {
    e.preventDefault();
    if (!url.trim() || busy) return;
    setBusy(true);
    setFailure(null);
    try {
      const result = await createSubmissionFromLink(tenantId, url.trim());
      onCreated(result.submission.id);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'link_unreadable') {
        setUnreadable(err.serverMessage ?? err.message);
      } else {
        setFailure(err instanceof ApiError ? err.message : 'that could not be read just now');
      }
    } finally {
      setBusy(false);
    }
  }

  async function submitManual(e: Event) {
    e.preventDefault();
    if (!opportunity.trim() || busy) return;
    setBusy(true);
    setFailure(null);
    try {
      // B20d: the manual path sends its own fields — the performer, the
      // contact (matched or created), the part/project (linked by exact
      // name when they exist) and the due date are all resolved server-side
      // by `attachSubmissionDefaults`. Nothing here is composed into
      // `notes` — that stays the private field.
      const submission = await createSubmission(tenantId, {
        opportunity: opportunity.trim(),
        contactName: contactName.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
        roleName: part.trim() || undefined,
        projectTitle: project.trim() || undefined,
        dueAt: due.trim() || undefined,
      });

      if (whereFound === 'tell' && statement.trim()) {
        await setSubmissionProvenance(tenantId, submission.id, {
          grade: 'statement',
          statement: statement.trim(),
        });
      } else if (whereFound === 'upload' && file) {
        const { mediaId } = await uploadMedia(tenantId, 'other', file, {});
        await setSubmissionProvenance(tenantId, submission.id, { grade: 'artifact', mediaId });
      }

      onCreated(submission.id);
    } catch (err) {
      setFailure(err instanceof ApiError ? err.message : 'that could not be saved just now');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="subsheet" role="dialog" aria-label="Submit for something">
      <div class="subsheet__head">
        <h2 class="subsheet__title">Submit for something</h2>
        <button type="button" class="subsheet__close" aria-label="Close" onClick={onClose}>
          ✕
        </button>
      </div>

      {unreadable === null ? (
        <form class="subsheet__field" onSubmit={submitLink}>
          <label class="subsheet__label" for="subsheet-url">
            Paste the link to the call
          </label>
          <input
            id="subsheet-url"
            class="subsheet__input"
            value={url}
            onInput={(e) => setUrl((e.target as HTMLInputElement).value)}
            placeholder="https://…"
          />
          <button type="submit" class="subsheet__btn subsheet__btn--go" disabled={busy || !url.trim()}>
            {busy ? 'Reading…' : 'Continue'}
          </button>
        </form>
      ) : (
        <>
          <p class="subsheet__say" data-testid="link-unreadable">
            {unreadable}
          </p>

          <form class="subsheet__manual" onSubmit={submitManual}>
            <label class="subsheet__label" for="subsheet-opportunity">
              Opportunity
            </label>
            <input
              id="subsheet-opportunity"
              class="subsheet__input"
              value={opportunity}
              onInput={(e) => setOpportunity((e.target as HTMLInputElement).value)}
            />

            <label class="subsheet__label" for="subsheet-project">
              Project
            </label>
            <input
              id="subsheet-project"
              class="subsheet__input"
              value={project}
              onInput={(e) => setProject((e.target as HTMLInputElement).value)}
            />

            <label class="subsheet__label" for="subsheet-part">
              Part
            </label>
            <input
              id="subsheet-part"
              class="subsheet__input"
              value={part}
              onInput={(e) => setPart((e.target as HTMLInputElement).value)}
            />

            <label class="subsheet__label" for="subsheet-contact-name">
              Contact name
            </label>
            <input
              id="subsheet-contact-name"
              class="subsheet__input"
              value={contactName}
              onInput={(e) => setContactName((e.target as HTMLInputElement).value)}
            />

            <label class="subsheet__label" for="subsheet-contact-email">
              Contact email
            </label>
            <input
              id="subsheet-contact-email"
              class="subsheet__input"
              value={contactEmail}
              onInput={(e) => setContactEmail((e.target as HTMLInputElement).value)}
            />

            <label class="subsheet__label" for="subsheet-due">
              Due
            </label>
            <input
              id="subsheet-due"
              class="subsheet__input"
              type="date"
              value={due}
              onInput={(e) => setDue((e.target as HTMLInputElement).value)}
            />

            <div class="subsheet__where">
              <p class="subsheet__label">Where did you find it?</p>
              <div class="subsheet__where-row">
                <button
                  type="button"
                  class={`subsheet__where-btn${whereFound === 'upload' ? ' subsheet__where-btn--on' : ''}`}
                  onClick={() => setWhereFound(whereFound === 'upload' ? null : 'upload')}
                >
                  Upload a screenshot or the email
                </button>
                <button
                  type="button"
                  class={`subsheet__where-btn${whereFound === 'tell' ? ' subsheet__where-btn--on' : ''}`}
                  onClick={() => setWhereFound(whereFound === 'tell' ? null : 'tell')}
                >
                  Tell us where
                </button>
              </div>
              {whereFound === 'upload' ? (
                <input
                  type="file"
                  accept="image/*,.eml,.msg,.pdf"
                  data-testid="subsheet-file"
                  onChange={(e) => setFile((e.target as HTMLInputElement).files?.[0] ?? null)}
                />
              ) : null}
              {whereFound === 'tell' ? (
                <input
                  class="subsheet__input"
                  data-testid="subsheet-statement"
                  value={statement}
                  onInput={(e) => setStatement((e.target as HTMLInputElement).value)}
                />
              ) : null}
            </div>

            <button type="submit" class="subsheet__btn subsheet__btn--go" disabled={busy || !opportunity.trim()}>
              {busy ? 'Saving…' : 'Start the submission'}
            </button>
          </form>
        </>
      )}

      {failure ? <p class="subsheet__say subsheet__say--loud">{failure}</p> : null}
    </div>
  );
}
